import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function BlogTeaser() {
  if (!blogPosts.length) return null;
  const [latest, ...rest] = blogPosts;
  const more = rest.slice(0, 2);

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="home-blog-teaser">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">From the Happy Blog</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
              Tips, stories & water safety
            </h2>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-2 font-bold text-[#0EA5E9] hover:gap-3 transition-all" data-testid="blog-teaser-viewall">
            View all articles <ArrowRight size={18} />
          </Link>
        </div>

        <Link to={`/blog/${latest.slug}`} data-testid="blog-teaser-featured">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-[#F8FAFC] rounded-[2rem] p-6 md:p-8 border border-slate-100 hover:shadow-xl transition-all duration-500"
          >
            <div className="rounded-[1.5rem] overflow-hidden">
              <img src={latest.heroImage} alt={latest.heroAlt} className="w-full h-60 md:h-72 object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-widest bg-[#0EA5E9]/10 text-[#0EA5E9] px-3 py-1.5 rounded-full">{latest.category}</span>
              <h3 className="mt-4 font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A] leading-tight group-hover:text-[#0EA5E9] transition-colors">
                {latest.title}
              </h3>
              <p className="mt-4 text-[#334155] leading-relaxed">{latest.excerpt}</p>
              <div className="mt-5 flex items-center gap-5 text-sm text-[#64748B]">
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {fmtDate(latest.date)}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {latest.readTime}</span>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 font-bold text-[#FF7A1A] group-hover:gap-3 transition-all">
                Read the story <ArrowRight size={18} />
              </span>
            </div>
          </motion.article>
        </Link>

        {more.length > 0 && (
          <div className="mt-8 grid sm:grid-cols-2 gap-6 md:gap-8">
            {more.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`blog-teaser-card-${post.slug}`}>
                <article className="group flex gap-5 bg-[#F8FAFC] rounded-[1.5rem] p-4 border border-slate-100 hover:shadow-lg transition-all duration-500 h-full">
                  <img src={post.heroImage} alt={post.heroAlt} className="w-28 h-28 rounded-2xl object-cover shrink-0" />
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9]">{post.category}</span>
                    <h3 className="mt-1 font-heading text-lg font-bold text-[#0F172A] leading-snug group-hover:text-[#0EA5E9] transition-colors line-clamp-3">{post.title}</h3>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
